import { DatabaseStorageHelper } from '../../../helpers';
import { STORAGE_KEYS } from '../utils/savedSearchConstants';
import Auth from '../../../auth';

class SavedSearchStorage {
  getCompanyId(): string {
    return Auth.getAccountNumber() || 'default';
  }

  async getDefaultSearchId(tableType: string): Promise<string | null> {
    const companyId = this.getCompanyId();
    const userId = Auth.getUserId();
    const key = `${STORAGE_KEYS.DEFAULT_PREFIX}${tableType}-${userId}-${companyId}`;
    try {
      const result = await new DatabaseStorageHelper().get(key);
      if (result) return result;

      // Migrate legacy dashboard default key (old system used 'lb-default-report')
      if (tableType === 'dashboard') {
        const legacyDefault = await new DatabaseStorageHelper().get('lb-default-report');
        if (legacyDefault) {
          // Migrate: save to new key and remove legacy key
          await new DatabaseStorageHelper().set(key, legacyDefault);
          await new DatabaseStorageHelper().remove('lb-default-report');
          return legacyDefault;
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting default search ID from storage:', error);
      return null;
    }
  }

  async setDefaultSearchId(tableType: string, searchId: string | number | null): Promise<void> {
    const companyId = this.getCompanyId();
    const userId = Auth.getUserId();
    const key = `${STORAGE_KEYS.DEFAULT_PREFIX}${tableType}-${userId}-${companyId}`;
    try {
      if (searchId) {
        await new DatabaseStorageHelper().set(key, searchId);
      } else {
        await new DatabaseStorageHelper().remove(key);
      }
      // Clean up legacy key if it exists
      if (tableType === 'dashboard') {
        try { await new DatabaseStorageHelper().remove('lb-default-report'); } catch (_) { /* ignore */ }
      }
    } catch (error) {
      console.error('Error setting default search ID:', error);
      throw error;
    }
  }

  getCompanyScopedSearchId(searchId: string): string {
    const companyId = this.getCompanyId();
    if (searchId && searchId.startsWith('savedSearch-')) {
      // Avoid double-scoping IDs that already have company suffix
      if (searchId.includes('-company-')) {
        return searchId;
      }
      return `${searchId}-company-${companyId}`;
    }
    return searchId;
  }

  async saveLocalSearch(searchId: string, searchData: any, skipScoping: boolean = false): Promise<void> {
    try {
      const id = skipScoping ? searchId : this.getCompanyScopedSearchId(searchId);
      await new DatabaseStorageHelper().set(id, searchData);
    } catch (error) {
      console.error('Error saving search to user data:', error);
      throw error;
    }
  }

  async deleteLocalSearch(searchId: string, skipScoping: boolean = false): Promise<void> {
    try {
      if (skipScoping) {
        await new DatabaseStorageHelper().remove(searchId);
        return;
      }
      // Existing fallback logic for table mode
      try {
        await new DatabaseStorageHelper().remove(searchId);
        return;
      } catch (err) {
        const scopedId = this.getCompanyScopedSearchId(searchId);
        if (scopedId !== searchId) {
          await new DatabaseStorageHelper().remove(scopedId);
        } else {
          throw err;
        }
      }
    } catch (error) {
      console.error('Error deleting search from user data:', error);
      throw error;
    }
  }

  getSearchIdFromURL(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('savedSearch');
  }

  hasManualFilters(): boolean {
    const params = new URLSearchParams(window.location.search);
    const filterKeys: string[] = Array.from(params.keys()).filter(key => key.startsWith('q.'));
    return filterKeys.length > 0;
  }

  clearURLFilters(): void {
    const params = new URLSearchParams(window.location.search);

    const keysToDelete: string[] = Array.from(params.keys()).filter(key => key.startsWith('q.'));
    keysToDelete.forEach(key => params.delete(key));

    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newURL);
  }

  updateURLWithSearchId(searchId: string | number | null, preserveFilters = false): void {
    const params = new URLSearchParams(window.location.search);

    // Only remove filter parameters if we're not preserving them
    if (!preserveFilters) {
      const keysToDelete: string[] = Array.from(params.keys()).filter(key => key.startsWith('q.'));
      keysToDelete.forEach(key => params.delete(key));
    }

    if (searchId) {
      // URLSearchParams.set() automatically encodes the value
      params.set('savedSearch', String(searchId));
    } else {
      params.delete('savedSearch');
    }

    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newURL);
  }
}

const savedSearchStorage = new SavedSearchStorage();
export default savedSearchStorage;
